'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
	forwardRef, PropsWithChildren, useImperativeHandle,
} from 'react';
import {
	FormProvider as ReactHookFormProvider, useForm, UseFormReturn,
} from 'react-hook-form';

import { FormConfigProvider } from '../../../providers';

import type { FormProviderProps } from './FormProvider.types';

export const FormProvider = forwardRef(<T extends Record<string, unknown>>(
	props: PropsWithChildren<FormProviderProps<T>>,
	ref: React.Ref<UseFormReturn<T>>,
) => {
	const {
		children,
		defaultValues,
		formConfig,
		mode = 'onChange',
		onSubmit,
		reValidateMode = 'onChange',
		schema,
	} = props;

	const methods = useForm<T>({
		defaultValues,
		mode,
		resolver: zodResolver(schema),
		reValidateMode,
	});

	useImperativeHandle(ref, () => methods, [ methods ]);

	return (
		<FormConfigProvider formConfig={formConfig}>
			<ReactHookFormProvider {...methods}>
				<form
					onReset={() => methods.reset(defaultValues)}
					onSubmit={methods.handleSubmit(onSubmit)}
					style={{ display: 'contents', minHeight: 0 }}
				>
					{children}
				</form>
			</ReactHookFormProvider>
		</FormConfigProvider>
	);
}) as unknown as <T extends Record<string, unknown>>(
	props: PropsWithChildren<FormProviderProps<T>> & {
		ref?: React.Ref<UseFormReturn<T>>
	},
) => React.ReactElement;
